import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async'; // <-- IMPORT

function ProfilePage() {
  const { memberId } = useParams();
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMemberProfile = async () => {
      try {
        const response = await fetch(`https://healthcard.searchisend.com/health_api/get_member_profile.php?id=${memberId}`);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Member not found.');
        }
        const data = await response.json();
        setMember(data);

        // SILENT BACKGROUND LOGGING: Log the scan when profile successfully loads
        fetch(`https://healthcard.searchisend.com/health_api/log_scan.php?id=${memberId}`).catch(err => console.error("Scan log failed", err));

      } catch (e) {
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMemberProfile();
  }, [memberId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) return <div className="text-center text-gray-700">Loading profile...</div>;
  if (error) return (
    <div className="flex flex-col items-center justify-center p-12 text-center bg-white rounded-3xl shadow-xl w-full max-w-lg mx-auto border-t-4 border-red-500 mt-10">
      <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-6">
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile Unavailable</h2>
      <p className="text-gray-500">{error}</p>
    </div>
  );

  // --- Prepare dynamic data ---
  const memberName = `${member.firstName} ${member.lastName}`;
  const memberProfession = member.profession || 'Member';
  const pageTitle = `${memberName} | Health Portal`;
  const pageDescription = `${memberProfession} at Health Card from ${member.city || ''}, ${member.state}.`;
  const profileImageUrl = member.profilePictureUrl
    ? `https://healthcard.searchisend.com${member.profilePictureUrl}`
    : `https://healthcard.searchisend.com/logo512.png`; // Fallback to main logo

  return (
    <>
      {/* --- THIS IS THE NEW SEO SECTION --- */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph (Facebook, WhatsApp) */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={profileImageUrl} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="profile" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={profileImageUrl} />
      </Helmet>
      {/* --- END OF SEO SECTION --- */}

      <div className="glass rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] w-full max-w-2xl overflow-hidden relative">

        {/* Animated Emergency Header */}
        <div className="bg-gradient-to-r from-brand-accent to-red-600 text-white p-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-full animate-heartbeat">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
            </div>
            <h2 className="font-bold text-xl tracking-wide">Emergency Profile</h2>
          </div>
          <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-bold tracking-wider uppercase">Active Scan</span>
        </div>

        <div className="p-8 md:p-10 flex flex-col items-center relative z-10 w-full">
          {/* Photo / ID */}
          <div className="flex flex-col items-center gap-4 mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-brand-primary opacity-20 blur-xl rounded-full"></div>
              <img
                src={member.profilePictureUrl ? `https://healthcard.searchisend.com${member.profilePictureUrl}` : `https://ui-avatars.com/api/?name=${member.firstName}+${member.lastName}&background=059669&color=fff&size=128`}
                alt={`${member.firstName} ${member.lastName}`}
                className="w-32 h-32 rounded-full object-cover shadow-xl border-4 border-white relative z-10"
                onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${member.firstName}+${member.lastName}&background=059669&color=fff&size=128` }}
              />
            </div>
            <div className="bg-brand-background px-4 py-1.5 rounded-full text-sm font-bold text-slate-500 shadow-inner border border-slate-200">
              ID: {member.memberId}
            </div>
          </div>

          {/* Details */}
          <div className="text-center w-full mb-8">
            <h1 className="text-4xl font-extrabold text-brand-secondary mb-2 tracking-tight">{member.firstName} {member.lastName}</h1>
            <p className="text-emerald-700 font-medium bg-emerald-50 border border-emerald-100 inline-block px-4 py-1.5 rounded-full text-sm">
              {member.profession || 'Member'} &bull; {member.city ? `${member.city}, ` : ''}{member.state}
            </p>
          </div>

          {/* Medical Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full mb-6">
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center md:items-start transition transform hover:-translate-y-1 hover:shadow-md">
              <span className="block text-sm text-slate-400 font-medium mb-1 uppercase tracking-wider">Blood Type</span>
              <span className="text-3xl font-black text-brand-accent">{member.bloodGroup || 'N/A'}</span>
            </div>
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center md:items-start transition transform hover:-translate-y-1 hover:shadow-md">
              <span className="block text-sm text-slate-400 font-medium mb-1 uppercase tracking-wider">Organ Donor</span>
              <span className={`text-xl font-bold mt-1 uppercase ${member.isOrganDonor == 1 ? 'text-brand-primary' : 'text-slate-400'}`}>
                {member.isOrganDonor == 1 ? 'YES' : 'NO'}
              </span>
            </div>
            <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center md:items-start transition transform hover:-translate-y-1 hover:shadow-md col-span-2 md:col-span-1">
              <span className="block text-sm text-slate-400 font-medium mb-1 uppercase tracking-wider">Status</span>
              <span className="text-xl font-bold text-brand-primary mt-1 capitalize flex items-center gap-1">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                Verified
              </span>
            </div>
          </div>

          {/* Critical Info Layout */}
          <div className="w-full space-y-4 mb-8">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 relative">
              <div className="absolute -top-3 left-6 bg-brand-secondary text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Allergies</div>
              <div className="mt-2 text-brand-secondary font-medium">
                {member.allergies || 'None Known'}
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 relative">
              <div className="absolute -top-3 left-6 bg-brand-secondary text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Medical Conditions</div>
              <div className="mt-2 text-brand-secondary font-medium whitespace-pre-wrap">
                {member.medicalConditions || 'None Known'}
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 relative">
              <div className="absolute -top-3 left-6 bg-brand-secondary text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Current Medications</div>
              <div className="mt-2 text-brand-secondary font-medium whitespace-pre-wrap">
                {member.currentMedications || 'None / Not Listed'}
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 relative">
              <div className="absolute -top-3 left-6 bg-brand-secondary text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Vaccinations</div>
              <div className="mt-2 text-brand-secondary font-medium whitespace-pre-wrap">
                {member.vaccinationRecords || 'Not Listed'}
              </div>
            </div>
          </div>

          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {/* Primary Care Physician */}
            {(member.primaryCarePhysicianName || member.primaryCarePhysicianPhone) && (
              <div className="bg-brand-primary/10 p-5 rounded-2xl border border-brand-primary/20">
                <h3 className="font-bold text-brand-secondary text-sm mb-2 flex items-center gap-2 uppercase tracking-wider">
                  <svg className="w-4 h-4 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
                  Primary Physician
                </h3>
                <div className="flex flex-col">
                  <span className="font-extrabold text-brand-secondary text-lg">{member.primaryCarePhysicianName || 'N/A'}</span>
                  {member.primaryCarePhysicianPhone && (
                    <a href={`tel:${member.primaryCarePhysicianPhone}`} className="text-brand-primary font-bold hover:underline">
                      {member.primaryCarePhysicianPhone}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Emergency Contacts */}
            {(member.emergencyContactName || member.emergencyContactPhone) && (
              <div className={`bg-red-50 p-5 rounded-2xl border border-red-100 ${(!member.primaryCarePhysicianName && !member.primaryCarePhysicianPhone) ? 'md:col-span-2' : ''}`}>
                <h3 className="font-bold text-brand-secondary text-sm mb-2 flex items-center gap-2 uppercase tracking-wider">
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                  Emergency Contact
                </h3>
                <div className="flex flex-col">
                  <span className="font-extrabold text-brand-secondary text-lg">{member.emergencyContactName || 'N/A'} <span className="text-sm text-slate-500 font-medium ml-1">({member.emergencyContactRelation || 'Relation N/A'})</span></span>
                  {member.emergencyContactPhone && (
                    <a href={`tel:${member.emergencyContactPhone}`} className="text-red-600 font-bold hover:underline">
                      {member.emergencyContactPhone}
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bio */}
          {member.bio && (
            <div className="w-full mt-6 text-center text-slate-500 italic">
              "{member.bio}"
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ProfilePage;